import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Determine runtimeConfig based on SDK variant extracted from bundle URL.
 * Mill SDK variants (web, desktop, dcc) use the /mill proxy path.
 */
const getVariantRuntimeConfig = (bundleUrl) => {
  const variantMatch = bundleUrl.match(/\/(\w+)_sdk/);
  const variant = variantMatch ? variantMatch[1] : null;

  // Mill SDK variants use the /mill proxy
  if (variant && ['web', 'desktop', 'dcc'].includes(variant)) {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return {
      backendEnabled: true,
      httpBaseUrl: '/mill',
      wsBaseUrl: `${wsProtocol}//${window.location.host}/mill`
    };
  }

  // Fallback for other streamed features (Silo's own SDK, etc.)
  return {
    backendEnabled: true,
    httpBaseUrl: '/plugins/sdk',
    wsBaseUrl: null
  };
};

/**
 * Hook for loading and managing streamed feature bundles
 * @param {string} bundleUrl - Base URL for feature bundle (e.g., 'http://sdk-container:3003/web_sdk')
 * @param {Object} containerRef - React ref to DOM container element
 * @returns {Object} { loading, error, mounted, mountFeature, unmountFeature }
 */
export function useStreamingFeature(bundleUrl, containerRef) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mounted, setMounted] = useState(false);
  
  const featureContainerRef = useRef(null);
  const unmountFnRef = useRef(null);
  const bundleLoadedRef = useRef(false);
  const containerIdRef = useRef(null);

  /**
   * Load JavaScript bundle
   */
  const loadScript = async (url) => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load script: ${response.status} ${response.statusText}`);
    }
    
    // Check content type to ensure we got JavaScript, not HTML
    const contentType = response.headers.get('content-type');
    if (contentType && !contentType.includes('javascript') && !contentType.includes('text/plain')) {
      const text = await response.text();
      // If it's HTML, it's likely an error page or index.html
      if (text.trim().startsWith('<!')) {
        throw new Error(`Server returned HTML instead of JavaScript. URL may not exist: ${url}`);
      }
    }
    
    const scriptText = await response.text();
    
    // Verify it's actually JavaScript by checking for common JS patterns
    if (scriptText.trim().startsWith('<!')) {
      throw new Error(`Server returned HTML instead of JavaScript. URL may not exist: ${url}`);
    }
    
    // Create a function that will execute the script in the global context
    // This allows the bundle to access window.parentApi
    const scriptFunc = new Function(scriptText);
    return scriptFunc;
  };

  /**
   * Load CSS bundle
   */
  const loadCSS = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        // CSS is optional, don't fail if not found
        return null;
      }
      const cssText = await response.text();
      return cssText;
    } catch (err) {
      // CSS is optional, ignore errors
      console.warn('[StreamingFeature] Failed to load CSS:', err);
      return null;
    }
  };


  /**
   * Mount feature bundle
   */
  const mountFeature = useCallback(async () => {
    if (!bundleUrl || !containerRef?.current || bundleLoadedRef.current) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      bundleLoadedRef.current = true;

      // Monaco Editor doesn't work properly inside Shadow DOM, so we mount directly
      // and use CSS scoping for style isolation instead
      const featureContainer = document.createElement('div');
      featureContainer.style.width = '100%';
      featureContainer.style.height = '100%';
      // Contain layout/paint/size to prevent scroll events from escaping to document
      featureContainer.style.contain = 'strict';
      featureContainer.style.overflow = 'auto';
      
      // Generate unique ID for CSS scoping
      const containerId = `streaming-feature-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      containerIdRef.current = containerId;
      featureContainer.id = containerId;
      featureContainer.className = 'streaming-feature-container';
      
      containerRef.current.appendChild(featureContainer);
      featureContainerRef.current = featureContainer;

      // Determine bundle URLs (try common patterns)
      // SDK bundles are named based on variant: silo-sdk-{variant}.iife.js
      // Extract variant from bundleUrl (e.g., /web_sdk -> web)
      const variantMatch = bundleUrl.match(/\/(\w+)_sdk/);
      const variant = variantMatch ? variantMatch[1] : 'web'; // default to web
      const cacheBust = `?t=${Date.now()}`;
      
      const jsUrls = [
        // SDK-specific bundle names (from integration guide)
        `${bundleUrl}/silo-sdk-${variant}.iife.js${cacheBust}`,
        `${bundleUrl}/silo-sdk-${variant}.js${cacheBust}`,
        // Common patterns
        `${bundleUrl}/index.js${cacheBust}`,
        `${bundleUrl}/bundle.js${cacheBust}`,
        `${bundleUrl}/main.js${cacheBust}`,
        `${bundleUrl}/app.js${cacheBust}`,
        `${bundleUrl}/silo-sdk.iife.js${cacheBust}`,
        `${bundleUrl}/silo-sdk.js${cacheBust}`
      ];
      
      // CSS bundle URLs (SDK bundles are named silo-sdk-{variant}.iife.css)
      const cssUrls = [
        // SDK-specific CSS bundle names
        `${bundleUrl}/silo-sdk-${variant}.iife.css`,
        `${bundleUrl}/silo-sdk-${variant}.css`,
        // Common patterns
        `${bundleUrl}/index.css`,
        `${bundleUrl}/styles.css`,
        `${bundleUrl}/main.css`
      ];

      // Try to load JavaScript bundle
      let scriptFunc = null;
      let scriptError = null;
      
      for (const url of jsUrls) {
        try {
          scriptFunc = await loadScript(url);
          break;
        } catch (err) {
          scriptError = err;
          continue;
        }
      }

      if (!scriptFunc) {
        throw new Error(`Failed to load JavaScript bundle. Tried: ${jsUrls.join(', ')}. Last error: ${scriptError?.message || 'Unknown'}`);
      }

      // Load CSS bundle BEFORE mounting (CSS must be available when SDK renders)
      // SDK's CSS is already scoped with .silo-sdk-root, so we load it directly
      for (const url of cssUrls) {
        try {
          const cssText = await loadCSS(url);
          if (cssText) {
            const style = document.createElement('style');
            style.textContent = cssText;
            style.setAttribute('data-streaming-feature-css', containerId);
            document.head.appendChild(style);
            break;
          }
        } catch (err) {
          // Continue to next URL
          continue;
        }
      }

      // Add Monaco editor CSS fixes to prevent cursor/selection issues from Silo's global styles
      const monacoFixStyle = document.createElement('style');
      monacoFixStyle.setAttribute('data-streaming-feature-css', containerId);
      monacoFixStyle.textContent = `
        /* Fix Monaco editor cursor and selection issues in Silo */
        #${containerId} .monaco-editor,
        #${containerId} .monaco-editor .view-line,
        #${containerId} .monaco-editor .view-lines,
        #${containerId} .monaco-editor .inputarea,
        #${containerId} .monaco-editor .lines-content {
          cursor: text !important;
          user-select: text !important;
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
        }
        #${containerId} .monaco-editor * {
          resize: none !important;
        }
        /* Prevent inherited cursor styles from affecting Monaco */
        #${containerId} .monaco-editor .cursor {
          cursor: text !important;
        }
        #${containerId} .monaco-editor .monaco-mouse-cursor-text {
          cursor: text !important;
        }
      `;
      document.head.appendChild(monacoFixStyle);

      // Execute script to expose mount function
      scriptFunc();

      // SDK exposes mount function on window.SiloSDK.mount
      // mount() returns an unmount function
      if (!window.SiloSDK || typeof window.SiloSDK.mount !== 'function') {
        throw new Error('Bundle did not expose window.SiloSDK.mount function. Ensure SDK bundle is loaded correctly.');
      }

      // Ensure Parent API is available
      if (!window.parentApi && !window.siloParentAPI) {
        throw new Error('Parent API not available. Ensure ParentApiProvider is initialized.');
      }

      const parentApi = window.parentApi || window.siloParentAPI;

      // Determine runtimeConfig based on SDK variant
      const variantRuntimeConfig = getVariantRuntimeConfig(bundleUrl);

      // Call mount function - it returns unmount function
      // SDK mount signature: mount(container, { hostApi, windowId, version, metadata, runtimeConfig })
      const unmountFn = window.SiloSDK.mount(featureContainer, {
        hostApi: parentApi, // SDK expects hostApi, we pass parentApi
        windowId: 'streamed-feature',
        version: 'published',
        metadata: {},
        runtimeConfig: variantRuntimeConfig
      });

      if (typeof unmountFn !== 'function') {
        throw new Error('Mount function did not return unmount function');
      }

      unmountFnRef.current = unmountFn;
      setMounted(true);

    } catch (err) {
      console.error('[StreamingFeature] Mount error:', err);
      setError(err.message || 'Failed to mount feature');
      bundleLoadedRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [bundleUrl, containerRef]);

  /**
   * Unmount feature bundle
   */
  const unmountFeature = useCallback(() => {
    if (unmountFnRef.current && typeof unmountFnRef.current === 'function') {
      try {
        unmountFnRef.current();
      } catch (err) {
        console.error('[StreamingFeature] Unmount error:', err);
      }
      unmountFnRef.current = null;
    }

    // Clean up container
    if (featureContainerRef.current && featureContainerRef.current.parentNode) {
      try {
        featureContainerRef.current.parentNode.removeChild(featureContainerRef.current);
      } catch (err) {
        console.warn('[StreamingFeature] Error removing container:', err);
      }
    }

    // Clean up CSS styles added to document head for this instance
    if (containerIdRef.current) {
      const styles = document.head.querySelectorAll(`style[data-streaming-feature-css="${containerIdRef.current}"]`);
      styles.forEach(style => style.remove());
      containerIdRef.current = null;
    }

    featureContainerRef.current = null;
    setMounted(false);
    bundleLoadedRef.current = false;
  }, []);

  // Mount on bundleUrl change
  useEffect(() => {
    if (bundleUrl && containerRef?.current) {
      mountFeature();
    }

    return () => {
      unmountFeature();
    };
  }, [bundleUrl]); // Only depend on bundleUrl, mountFeature/unmountFeature are stable

  return {
    loading,
    error,
    mounted,
    mountFeature,
    unmountFeature
  };
}

